import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './CategoryControls.css';

const CategoryControls = ({ onSearch, onSort, onFilter, businessCount, initialSearchQuery }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    price: [],
    features: [],
    rating: null,
    verified: false,
    openNow: false
  });

  useEffect(() => {
    setSearchQuery(initialSearchQuery || '');
  }, [initialSearchQuery]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearch(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch(searchQuery);
    }
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...selectedFilters };
    
    if (filterType === 'price' || filterType === 'features') {
      const currentValues = newFilters[filterType] || [];
      if (currentValues.includes(value)) {
        newFilters[filterType] = currentValues.filter(item => item !== value);
      } else {
        newFilters[filterType] = [...currentValues, value];
      }
    } else {
      newFilters[filterType] = value;
    }
    
    setSelectedFilters(newFilters);
  };

  const applyFilters = () => {
    onFilter(selectedFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const clearedFilters = {
      price: [],
      features: [],
      rating: null,
      verified: false,
      openNow: false
    };
    setSelectedFilters(clearedFilters);
    onFilter(clearedFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedFilters.price.length > 0) count++;
    if (selectedFilters.features.length > 0) count++;
    if (selectedFilters.rating) count++;
    if (selectedFilters.verified) count++;
    if (selectedFilters.openNow) count++;
    return count;
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSortChange = (e) => {
    onSort(e.target.value);
  };

  return (
    <div className="category-controls">
      <div className="controls-container">
        {/* Search Bar */}
        <div className="search-section">
          <div className="search-input-wrapper" style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            transition: 'border-color 0.2s ease',
            height: '42px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '14px',
              paddingRight: '6px',
              height: '100%'
            }}>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#9CA3AF" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ display: 'block', flexShrink: 0 }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <input
              type="text"
              placeholder={`Search ${businessCount}+ businesses...`}
              value={searchQuery}
              onChange={handleSearch}
              onKeyPress={handleKeyPress}
              className="search-input"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                padding: '0 8px',
                height: '100%',
                fontSize: '15px',
                backgroundColor: 'transparent',
                color: '#1F2937',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
              onFocus={(e) => e.target.parentElement.style.borderColor = '#4F46E5'}
              onBlur={(e) => e.target.parentElement.style.borderColor = '#e5e7eb'}
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => {
                  setSearchQuery('');
                  onSearch('');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 14px',
                  height: '100%',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  fontSize: '18px',
                  transition: 'color 0.2s',
                  flexShrink: 0
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Sort & Filter Controls */}
        <div className="controls-section">
          <div className="sort-control">
            <select 
              onChange={handleSortChange}
              className="sort-select"
              defaultValue="relevance"
            >
              <option value="relevance">Sort: Relevance</option>
              <option value="rating">Sort: Rating</option>
              <option value="distance">Sort: Distance</option>
              <option value="reviews">Sort: Reviews</option>
              <option value="newest">Sort: Newest</option>
            </select>
          </div>

          <button 
            className="filter-toggle"
            onClick={toggleFilters}
            data-active={showFilters}
          >
            <span className="filter-icon">⚙️</span>
            <span className="filter-text">
              Filters
              {getActiveFiltersCount() > 0 && (
                <span className="filter-count">{getActiveFiltersCount()}</span>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Expandable Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header">
            <h3 className="filters-title">Filter Businesses</h3>
            <div className="filter-actions-header">
              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear All
              </button>
            </div>
          </div>
          
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">Price Range</label>
              <div className="checkbox-group">
                {['$', '$$', '$$$'].map(price => (
                  <label key={price} className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="price" 
                      value={price}
                      checked={selectedFilters.price.includes(price)}
                      onChange={() => handleFilterChange('price', price)}
                    />
                    <span className="checkbox-text">{price}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Features</label>
              <div className="checkbox-group">
                {['Delivery', 'Takeaway', 'Reservation', 'Parking', 'AC', 'WiFi'].map(feature => (
                  <label key={feature} className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="features" 
                      value={feature.toLowerCase()}
                      checked={selectedFilters.features.includes(feature.toLowerCase())}
                      onChange={() => handleFilterChange('features', feature.toLowerCase())}
                    />
                    <span className="checkbox-text">{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Rating</label>
              <div className="radio-group">
                {[4.5, 4.0, 3.5, 3.0].map(rating => (
                  <label key={rating} className="radio-label">
                    <input 
                      type="radio" 
                      name="rating" 
                      value={rating}
                      checked={selectedFilters.rating === rating}
                      onChange={() => handleFilterChange('rating', rating)}
                    />
                    <span className="radio-text">{rating}+ ⭐</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Status</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="verified" 
                    value="verified"
                    checked={selectedFilters.verified}
                    onChange={() => handleFilterChange('verified', !selectedFilters.verified)}
                  />
                  <span className="checkbox-text">✓ Verified Only</span>
                </label>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="open" 
                    value="open"
                    checked={selectedFilters.openNow}
                    onChange={() => handleFilterChange('openNow', !selectedFilters.openNow)}
                  />
                  <span className="checkbox-text">🟢 Open Now</span>
                </label>
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters ({getActiveFiltersCount()})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CategoryControls.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  businessCount: PropTypes.number.isRequired,
  initialSearchQuery: PropTypes.string
};

export default CategoryControls;